import { useState, FormEvent } from 'react';
import { observer } from 'mobx-react-lite';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Input, LanguageSelector } from '@emfit/shared-ui';
import { SessionStore } from '../stores';
import { LocalizationService, t } from '../services/LocalizationService';
import { SUPPORTED_LANGUAGES, Locales } from '../config';
import LOGO from '../img/emfit_care_login.png';

export const LoginPage = observer(() => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await SessionStore.login(
        username,
        password,
        twoFactorCode || undefined
      );

      if (result.success) {
        // Login successful
        setShow2FAModal(false);
        setTwoFactorCode('');
        navigate('/');
      } else if (result.requires2FA) {
        // Show 2FA modal
        setPhoneNumber(result.phoneNumber || '');
        setShow2FAModal(true);
        setLoading(false);
        return;
      } else {
        setError(result.error || t('Invalid username or password'));
        setShow2FAModal(false);
        setTwoFactorCode('');
      }
    } catch (err) {
      setError(t('An error occurred during login'));
      console.error('Login error:', err);
      setShow2FAModal(false);
      setTwoFactorCode('');
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (twoFactorCode.trim()) {
      handleSubmit(e);
    }
  };

  const handleLanguageChange = (languageCode: string) => {
    LocalizationService.locale = languageCode as Locales;
  };

  const handle2FACancel = () => {
    setShow2FAModal(false);
    setTwoFactorCode('');
    setError('');
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center w-full p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img src={LOGO} alt="Emfit Care" className="w-72 h-auto dark:invert" />
        </div>

        {/* Language selection — available before sign-in, persisted across reloads */}
        <div className="flex justify-end mb-2">
          <LanguageSelector
            languages={SUPPORTED_LANGUAGES}
            currentLanguage={LocalizationService.locale}
            onLanguageChange={handleLanguageChange}
            ariaLabel={t('Language')}
            alwaysShowLabel
          />
        </div>

        {/* Login Card */}
        <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold mb-6 text-center text-gray-900 dark:text-gray-100">
            {t('Login')}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username */}
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2"
              >
                {t('Username')}
              </label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t('Username')}
                fullWidth
                required
                disabled={loading}
                autoComplete="username"
                autoFocus
              />
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2"
              >
                {t('Password')}
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('Password')}
                fullWidth
                required
                disabled={loading}
                autoComplete="current-password"
                error={error}
              />
            </div>

            {error && (
              <div role="alert" className="bg-error-50 dark:bg-error-900/10 border border-error-200 dark:border-error-800 rounded-md p-3">
                <p className="text-sm text-error-600 dark:text-error-400">
                  {error}
                </p>
              </div>
            )}

            {/* Login Button */}
            <Button
              type="submit"
              variant="primary"
              fullWidth
              loading={loading}
              disabled={loading || !username || !password}
            >
              {t('Login')}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
            {t('Do not have an account yet?')}{' '}
            <Link
              to="/create-account"
              className="text-blue-600 hover:underline dark:text-blue-400"
            >
              {t('Create an account')}
            </Link>
          </p>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          Emfit Care &copy; {new Date().getFullYear()}
        </p>
      </div>

      {/* 2FA Modal */}
      {show2FAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full mx-4 max-h-full overflow-y-auto border border-gray-200 dark:border-gray-700">
            <form onSubmit={handle2FASubmit} className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {t('Two-factor Authentication')}
              </h2>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {t('We have sent a code to your phone via SMS.')}
              </p>

              {phoneNumber && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {t('Phone')}: <span className="font-mono">{phoneNumber}</span>
                </p>
              )}

              <div className="mb-6">
                <label
                  htmlFor="code"
                  className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2"
                >
                  {t('Verification Code')}
                </label>
                <Input
                  id="code"
                  type="text"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  placeholder={t('Enter 6-digit code')}
                  fullWidth
                  required
                  autoFocus
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  maxLength={6}
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handle2FACancel}
                  disabled={loading}
                >
                  {t('Cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={loading}
                  disabled={loading || !twoFactorCode.trim()}
                >
                  {t('Verify')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
});
